import React, { useEffect, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useDispatch } from "react-redux";
import { fetchRecordedCalls } from "../../../../store/RecordedCalls/recordedCallsThunks";
import ActionCell from "./ActionCell";
import DetailPanelContent from "./DetailPanelContent";
import PlayPanelContentWithComments from "./PlayPanelContentWithComments";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import CallIcon from "@mui/icons-material/Call";
import SideModal from "../../../../components/SideModal";
import CreatePlaylistForm from "../../../Playlist/Components/CreatePlaylistForm/CreatePlaylistForm";

function RecordedCallDataGrid({
  data,
  loading,
  apiRef,
  selectedIds,
  setSelectedIds,
  filterModel,
  setFilterModel,
}) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [sortModel, setSortModel] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [openCreatePlaylist, setOpenCreatePlaylist] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [rows, setRows] = useState([]);
  const [playRowId, setPlayRowId] = useState(null);
  const [participantsRowId, setParticipantsRowId] = useState(null);

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    const validFilters = newFilterModel.items.filter(
      (filter) => filter.value !== undefined && filter.value !== ""
    );
    if (validFilters.length > 0) {
      dispatch(fetchRecordedCalls({ filters: validFilters, sortModel: [] }));
    }
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    const validFilters = filterModel.items.filter(
      (filter) => filter.value !== undefined && filter.value !== ""
    );
    dispatch(
      fetchRecordedCalls({ filters: validFilters, sortModel: newSortModel })
    );
  };

  useEffect(() => {
    if (!Array.isArray(data)) {
      setRows([]);
      return;
    }
    const transformedRows = data.map((call) => {
      const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        });
      };

      return {
        id: call.id,
        channel: call.channel || "",
        startTime: formatDate(call.startTime),
        endTime: formatDate(call.endTime),
        owner: call.owner || "",
        retentionUntil: call.retentionUntil,
        retainPermanently: call.retainPermanently,
        retentionDisplay: call.retainPermanently
          ? "Permanent"
          : formatDate(call.retentionUntil),
        participants: call.participants || [],
        participantsText: (call.participants || [])
          .map(
            (p) =>
              p.recordedContactName ||
              p.thirdPartyContactName ||
              p.endpointIdentifier ||
              "Unknown"
          )
          .join(", "),
        isPrivate: call.isPrivate || false,
        route: call.route || "",
        area: call.area || "",
        function: call.function || "",
      };
    });
    setRows(transformedRows);
  }, [data]);

  const handleDetailPanelContent = ({ row }) => {
    if (row.id === playRowId) {
      return (
        <PlayPanelContentWithComments
          row={row}
          onClose={() => setPlayRowId(null)}
        />
      );
    }
    if (row.id === participantsRowId) {
      return (
        <DetailPanelContent
          row={row}
          onClose={() => setParticipantsRowId(null)}
        />
      );
    }
    return null;
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.2,
      minWidth: 30,
    },
    {
      field: "channel",
      headerName: "Channel",
      flex: 0.5,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const channel = params.value;
        let IconComponent = CallIcon;
        if (channel.toUpperCase() === "MOBILE") {
          IconComponent = PhoneAndroidIcon;
        } else if (channel.toUpperCase() === "TEAMS") {
          IconComponent = VideoCallIcon;
        }
        return <IconComponent />;
      },
    },
    {
      field: "startTime",
      headerName: "Start Time",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "endTime",
      headerName: "End Time",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "owner",
      headerName: "Owner",
      flex: 1,
      minWidth: 170,
    },
    {
      field: "participantsText",
      headerName: "Participants",
      flex: 1.2,
      minWidth: 250,
      renderCell: (params) => (
        <div style={{ whiteSpace: "normal", lineHeight: "1.2" }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "retentionDisplay",
      headerName: "Retention Until",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "isPrivate",
      headerName: "Is Private",
      flex: 0.8,
      minWidth: 180,
    },
    {
      field: "route",
      headerName: "Route",
      flex: 0.8,
      minWidth: 180,
    },
    {
      field: "area",
      headerName: "Area",
      flex: 0.8,
      minWidth: 180,
    },
    {
      field: "function",
      headerName: "Function",
      flex: 0.8,
      minWidth: 180,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <ActionCell
          id={params.row.id}
          row={params.row}
          onPlay={setPlayRowId}
          onShowParticipants={(id) => {
            if (participantsRowId === id) {
              setParticipantsRowId(null);
            } else {
              setParticipantsRowId(id);
              setPlayRowId(null);
            }
          }}
        />
      ),
      sortable: false,
      filterable: false,
      headerAlign: "center",
      align: "left",
    },
  ];

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <DataGridPro
        apiRef={apiRef}
        checkboxSelection
        disableRowSelectionOnClick
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        rowSelectionModel={selectedIds}
        onRowSelectionModelChange={setSelectedIds}
        rows={rows}
        columns={columns}
        loading={loading}
        sortingMode="server"
        filterMode="server"
        hideFooter
        onSortModelChange={handleSortModelChange}
        getRowHeight={() => "auto"}
        getDetailPanelHeight={() => "auto"}
        getDetailPanelContent={handleDetailPanelContent}
        detailPanelExpandedRowIds={[playRowId, participantsRowId].filter(
          Boolean
        )}
        hideDetailPanelToggle={true}
        slotProps={{
          filterPanel: {
            sx: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              "& .MuiButton-root": {
                backgroundColor: (theme) => theme.palette.background.button,
                color: (theme) => theme.palette.common.white,
                textTransform: "none",
                px: 2,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.background.button,
                },
              },
              "& .MuiIconButton-root": {
                color: theme.palette.text.primary,
              },
            },
          },
          columnsPanel: {
            sx: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            },
          },
        }}
        initialState={{
          columns: {
            columnVisibilityModel: {
              __detail_panel_toggle__: false,
              route: false,
              area: false,
              function: false,
            },
          },
        }}
        sx={{
          width: "100%",
          height: "100%",
        }}
      />
      {openCreatePlaylist && (
        <SideModal
          open={openCreatePlaylist}
          onClose={() => setOpenCreatePlaylist(false)}
          title="Create Playlist"
        >
          <CreatePlaylistForm
            filterModel={filterModel}
            selectedIds={selectionModel}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            onSubmit={(data) => {
              setOpenCreatePlaylist(false);
            }}
          />
        </SideModal>
      )}
    </Box>
  );
}

export default RecordedCallDataGrid;
